import makeActionCreator from 'src/lib/makeActionCreator';
import { State } from 'src/redux/reducers/RootReducer';
import {
  activateFundraisingData,
  deleteFundraisingData,
  getFundraisingData, getFundraisingListData,
  postFundraisingInfoData,
  putFundraisingAmbassadorsData,
  putFundraisingAssetsData,
  putFundraisingInfoData, putFundraisingNotificationsData,
  putFundraisingProductsData,
} from 'src/apis/FundraisingAPI';
import { setAlert } from 'src/redux/actions/alertActions';
import { ALERT_TYPE } from 'src/constants/AlertType';

import Debug from 'src/lib/Debug';
import {
  FundraisingAmbassadors,
  FundraisingAssets,
  FundraisingInfo,
  FundraisingMatchingInfo,
  FundraisingModel,
  FundraisingNotifications,
  FundraisingPayload,
  FundraisingProductsInfo,
} from 'src/models/FundraisingModel';
import {
  AMBASSADORS_KEYS,
  FUNDRAISING_INFO_KEYS,
  MATCHING_INFO_KEYS,
  NOTIFICATIONS_KEYS,
  pick,
  PRODUCTS_KEYS,
} from 'src/constants/Fields';
import { setError } from 'src/redux/actions/errorsActions';
import { FORM, FORM_STATUS } from 'src/constants/Form';
import { setFormStatus } from 'src/redux/actions/formActions';
import { getEntitiesAction } from 'src/redux/actions/querybuilderActions';
import { ENTITIES } from 'src/models/QuerybuilderModel';

export const ADD_FUNDRAISING = 'ADD_FUNDRAISING';
export const REMOVE_FUNDRAISING = 'REMOVE_FUNDRAISING';

export const addFundraising = makeActionCreator(ADD_FUNDRAISING);
export const removeFundraising = makeActionCreator(REMOVE_FUNDRAISING);

export const normalizeFundraisingData = (rawData: any) => {
  const data:any = {
    ...rawData,
    matching_info: { ...rawData.matching_info },
    matching_assets: { ...rawData.matching_assets },
  };
  return {
    fundraisingInfo:
      pick<FundraisingInfo, typeof FUNDRAISING_INFO_KEYS>(data, FUNDRAISING_INFO_KEYS) || null,
    matchingInfo:
    // eslint-disable-next-line max-len
      pick<FundraisingMatchingInfo, typeof MATCHING_INFO_KEYS>(data.matching_info, MATCHING_INFO_KEYS) || null,
    products:
      pick<FundraisingProductsInfo, typeof PRODUCTS_KEYS>(data, PRODUCTS_KEYS) || null,
    ambassadors:
      pick<FundraisingAmbassadors, typeof AMBASSADORS_KEYS>(data, AMBASSADORS_KEYS) || null,
    notifications:
      pick<FundraisingNotifications, typeof NOTIFICATIONS_KEYS>(data, NOTIFICATIONS_KEYS) || null,
    assets:
      typeof data.matching_assets === 'string' ? data.matching_assets : (data.matching_assets as FundraisingAssets) || null,
  };
};

type Callback = (newState: FundraisingModel) => void;

export const getFundraisingListAction: () => ThunkedAction<State> =
  () => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await getFundraisingListData(organisation);
      if (response.success) {
        response.data.each((data:any) => {
          const fundraising = normalizeFundraisingData({
            ...data,
            matching_info: { ...data.matching_info },
          } as FundraisingPayload);
          dispatch(addFundraising({ id: fundraising.fundraisingInfo.id, fundraising }));
        });
      }
    } catch (e) {
      Debug.errorLog(e);
    }
  };
export const getFundraisingAction : (id: string | number) => ThunkedAction<State> =
  (id: string | number) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await getFundraisingData(organisation, id);
      if (response.success) {
        const data = {
          ...response.data,
          matching_info: { ...response.data.matching_info },
        };
        const fundraising = normalizeFundraisingData(data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
      }
    } catch (e) {
      Debug.errorLog(e);
    }
  };

export const deleteFundraisingAction : (id: string) => ThunkedAction<State> =
  (id: string) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await deleteFundraisingData(organisation, id);
      if (response.success) {
        dispatch(removeFundraising(id));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully deleted',
        }));
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error deleting fundraising',
      }));
    } catch (e) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error deleting fundraising',
      }));
      Debug.errorLog(e);
    }
  };

export const activateFundraisingAction : (id: string, state: boolean) => ThunkedAction<State> =
  (id: string, state: boolean) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const response = await deleteFundraisingData(organisation, id);
      if (response.success) {
        dispatch(activateFundraisingData(organisation, id, state));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully activated/deactivated',
        }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error activating/deactivating fundraising',
      }));
    } catch (e) {
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error activating/deactivating fundraising',
      }));
      Debug.errorLog(e);
    }
  };

export const postFundraisingInfoAction: (
  data: FundraisingModel, callback?: Callback) => ThunkedAction<State> =
  (data: FundraisingModel, callback?: Callback) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const payload = {
        ...data.fundraisingInfo,
        matching_info: data.matchingInfo,
      } as FundraisingInfo | FundraisingMatchingInfo;
      const response = await postFundraisingInfoData(organisation, payload);
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
      if (response.success) {
        const { id } = response.data;
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(response.data as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
        return;
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingInfoAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const payload = {
        ...data.fundraisingInfo,
        matching_info: data.matchingInfo,
      } as FundraisingInfo | FundraisingMatchingInfo;
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.processing }));
      const response = await putFundraisingInfoData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.success }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(response.data as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
      dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingProductsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const payload = {
        ...data.products,
      } as FundraisingProductsInfo;
      const response = await putFundraisingProductsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(response.data as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingAmbassadorsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const payload = {
        ...data.ambassadors,
      };
      const response = await putFundraisingAmbassadorsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(response.data as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingNotificationsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const payload = {
        ...data.notifications,
      } as FundraisingNotifications;
      const response = await putFundraisingNotificationsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(response.data as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };

export const putFundraisingAssetsAction: (
  id: number, data: FundraisingModel, callback?: Callback
) => ThunkedAction<State> =
  (
    id: number, data: FundraisingModel, callback?: Callback,
  ) => async (dispatch: any, getState: any) => {
    try {
      const organisation = getState().currentOrganisation.id;
      const payload = {
        ...data.assets,
      } as FundraisingAssets;
      const response = await putFundraisingAssetsData(organisation, id, payload);
      if (response.success) {
        const fundraising = normalizeFundraisingData(response.data as FundraisingPayload);
        dispatch(addFundraising({ id, fundraising }));
        dispatch(setAlert({
          type: ALERT_TYPE.success,
          message: 'Fundraising successfully saved',
        }));
        if (callback) {
          callback(response.data as FundraisingModel);
        }
        dispatch(getEntitiesAction(ENTITIES.fundraisings));
        return;
      }
      if (response.status === 400) {
        dispatch(setError({ [FORM.fundraising]: response.errors.fields }));
        dispatch(setFormStatus({ [FORM.fundraising]: FORM_STATUS.error }));
      }
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    } catch (e) { /* Log the error here */
      Debug.errorLog(e);
      dispatch(setAlert({
        type: ALERT_TYPE.error,
        message: 'Error saving fundraising',
      }));
    }
  };
